import { useEffect, useId, useState } from 'react';
import { Alert, Button, Modal } from '@/components/ui';
import { ReportsService } from '@/services/reports.service';
import { getWriteErrorMessage } from '@/utils/apiError';
import type { ManagedFileReport } from '@/types/reports.types';
import { FileReportFilePicker } from './FileReportFilePicker';
import { validateFile } from './fileReportRules';

interface ReplaceFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  report: ManagedFileReport | null;
  onReplaced: (caption: string) => void;
}

export function ReplaceFileDialog({ isOpen, onClose, report, onReplaced }: ReplaceFileDialogProps) {
  const formId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateFile(file);
    setFileError(error);
    if (error || !report || !file) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await ReportsService.replaceFile(report.pageId, file);
      onReplaced(report.fileCaption);
      onClose();
    } catch (err) {
      setSubmitError(getWriteErrorMessage(err, 'The file could not be replaced.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Replace file"
      showCloseButton
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={saving} className="min-w-24">
            {saving ? 'Uploading…' : 'Replace'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        {submitError && <Alert variant="error">{submitError}</Alert>}
        {report && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Replaces <span className="font-medium">{report.fileName}</span> on{' '}
            <span className="font-medium">{report.fileCaption}</span>. The caption, category and
            other details stay the same.
          </p>
        )}
        <FileReportFilePicker
          label="New file"
          file={file}
          onChange={(next) => {
            setFile(next);
            setFileError(next ? validateFile(next) : null);
          }}
          error={fileError ?? undefined}
          disabled={saving}
        />
      </form>
    </Modal>
  );
}
